/**
 * /r/:code — Public referral landing page.
 * ============================================
 * Visitor lands here from a power user's share link. Shows social
 * proof ("Sarah saved $42K") + a single "Start your savings" CTA
 * that pre-stamps a `cartie_ref` cookie before navigating to login.
 *
 * Brand: Slate-950 base, emerald headline, cyan accents, soft amber for warnings.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEOHead from '../components/SEOHead';
import {
  Sparkles, ArrowRight, ShieldCheck, TrendingUp, Loader2,
  AlertTriangle,
} from 'lucide-react';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 30-day cookie so the user has time to come back and sign up.
function setRefCookie(code) {
  try {
    const max = 60 * 60 * 24 * 30;
    document.cookie = `cartie_ref=${encodeURIComponent(code)}; max-age=${max}; path=/; SameSite=Lax`;
    localStorage.setItem('cartie_ref', code);  // belt-and-suspenders
  } catch { /* */ }
}

const ReferralLanding = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/referrals/landing/${code}`);
        if (!alive) return;
        setData(data);
        setRefCookie(code);
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.status === 404
          ? 'This referral link is no longer active.'
          : 'Could not load this referral.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [code]);

  const goSignup = () => { setRefCookie(code); navigate('/login?signup=1'); };

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-slate-900 flex items-center justify-center">
        <div className="flex items-center gap-2 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading invite…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white text-slate-900 flex items-center justify-center px-4">
        <Card className="bg-slate-900 text-slate-100 border-amber-500/30 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-white mb-2"><BrandTitle>Invite not found</BrandTitle></h1>
            <p className="text-slate-400 text-sm mb-5">{error}</p>
            <Link to="/">
              <Button className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="ref-error-home-btn">
                Go to CARTIE.ai
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="referral-landing">
      <SEOHead
        title={`${data.referrer_name} invites you to CARTIEAI`}
        description={data.social_proof_line}
      />

      <header className="sticky top-0 z-30 bg-slate-950 text-slate-100 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="text-sm text-slate-400 hover:text-white">CARTIEAI</Link>
          <Button
            size="sm"
            onClick={goSignup}
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
            data-testid="ref-nav-signup"
          >
            Start free <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-16 sm:py-24">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/30 mb-5">
            <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
            <span className="text-[11px] font-semibold tracking-widest text-emerald-200 uppercase">
              You've been invited
            </span>
          </div>

          <h1 className="text-4xl sm:text-6xl font-bold tracking-tight">
            <span className="text-white">{data.referrer_name}</span>
            <span className="block text-emerald-400 mt-2">
              invited you to CARTIEAI
            </span>
          </h1>

          {data.referrer_savings_usd > 0 && (
            <div className="mt-8" data-testid="ref-social-proof">
              <p className="text-slate-400 text-base sm:text-lg">
                They've already saved
              </p>
              <p className="text-5xl sm:text-7xl font-bold text-emerald-300 my-3 tracking-tight">
                ${data.referrer_savings_usd.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </p>
              <p className="text-slate-400 text-sm">
                of verified cloud + AI cost on CARTIE.
              </p>
            </div>
          )}

          <p className="text-slate-600 mt-8 max-w-xl mx-auto leading-relaxed">
            CARTIEAI is the Financial Operating System for cloud + AI.
            See exactly which features, customers, and commitments are losing
            you money — then fix them with deterministic math, not guesswork.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              size="lg"
              onClick={goSignup}
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-base px-7 font-semibold"
              data-testid="ref-primary-cta"
            >
              Start saving — free <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <Link to="/savings-calculator">
              <Button
                size="lg"
                variant="outline"
                className="border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/10"
                data-testid="ref-calc-cta"
              >
                <TrendingUp className="w-4 h-4 mr-1.5" /> Estimate my savings first
              </Button>
            </Link>
          </div>

          <div className="grid sm:grid-cols-3 gap-3 mt-12 text-left">
            {[
              { icon: '⚡', text: 'Connect Stripe + cloud · 5 min setup' },
              { icon: '💰', text: 'Outcome pricing · only pay on verified savings' },
              { icon: '🛡️', text: 'Read-only · we never modify your infra' },
            ].map((b) => (
              <div key={b.text} className="bg-slate-900 text-slate-100 border border-white/10 rounded-xl p-4 flex items-start gap-3">
                <span className="text-xl flex-shrink-0">{b.icon}</span>
                <span className="text-slate-400 text-sm leading-snug">{b.text}</span>
              </div>
            ))}
          </div>

          <div className="mt-10 text-xs text-slate-400 inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
            Both you and {data.referrer_name} get a public credit when you cross $5K saved.
          </div>
        </div>
      </main>
    </div>
  );
};

export default ReferralLanding;
